import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Settings } from 'lucide-react'
import {
  fetchArrangement,
  fetchDashboard,
  greetingNow,
  routeOf,
  saveArrangement,
  tintOf,
  titleOf,
  type ArrangeableTile,
  type DashboardItem,
} from '../api/dashboard'
import { formatFullDate, formatTime } from '../api/calendar'
import { describeClass } from '../api/classDescription'
import { useSession } from '../useSession'
import DashboardArrange from './DashboardArrange'

/**
 * What needs her today (D-877 to D-883).
 *
 * **The tile is the one Tonya approved before the build started** —
 * `docs/cda-mockups.html`, *"Your morning, in one place"*: a card with a
 * coloured rail, a small label, a large number and a line under it saying what
 * the number is about.
 *
 * **The empty state is the screen's normal condition, not an edge case.** A
 * tile counting zero never arrives (D-882), so a studio in order renders
 * *nothing* — the good day — and the one thing that must never happen is the
 * best possible outcome looking like a page that failed to load.
 */
export default function DashboardView() {
  const { session } = useSession()
  const queryClient = useQueryClient()
  const [arranging, setArranging] = useState<ArrangeableTile[] | null>(null)

  const mayArrange = session?.capabilities.includes('ArrangeDashboard') ?? false

  const what = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    // She acts on a tile and comes straight back; a stale count would have her
    // chasing something she has just cleared.
    refetchOnWindowFocus: 'always',
  })

  /*
    Fetched when she reaches for the cog rather than on every load, because the
    dashboard itself never needs it: it is the *whole* set of tiles, quiet ones
    included, and every quiet tile costs a query nobody is going to read.
  */
  const open = useMutation({
    mutationFn: fetchArrangement,
    onSuccess: setArranging,
  })

  const save = useMutation({
    mutationFn: saveArrangement,
    onSuccess: async () => {
      setArranging(null)
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const tiles = what.data?.rows ?? []

  return (
    <div className="page-shell px-5 py-8">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl text-ink">
            {/*
              Her name, the way the deck opened. `displayName` can be absent on
              an account linked to nobody yet, and a greeting addressed to
              nobody reads worse than no greeting.

              The word follows the reader's clock, never the server's (D-885);
              the date on the line below follows the studio's.
            */}
            {session?.displayName
              ? `${greetingNow()}, ${session.displayName.split(' ')[0]}`
              : 'What needs you'}
          </h1>

          {what.data?.studio && (
            <p className="mt-0.5 text-[13px] text-ink-3">
              {formatFullDate(what.data.today)}
              {' · '}
              {what.data.studio.classesThisWeek === 1
                ? '1 class this week'
                : `${what.data.studio.classesThisWeek} classes this week`}
            </p>
          )}
        </div>

        {mayArrange && arranging === null && (
          <button
            type="button"
            onClick={() => open.mutate()}
            disabled={open.isPending}
            aria-label="Arrange this screen"
            title="Arrange"
            className="ml-auto grid size-8 place-items-center rounded-full border border-line-2 bg-surface text-ink-2 hover:border-ink-3"
          >
            <Settings size={16} />
          </button>
        )}

        {arranging !== null && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setArranging(null)}
              className="rounded-lg border border-line-2 bg-surface px-3 py-1 text-[13.5px] text-ink hover:border-ink-3"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => save.mutate(arranging)}
              disabled={save.isPending}
              className="rounded-lg border border-accent bg-accent px-3 py-1 text-[13.5px] font-semibold text-on-accent"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {arranging !== null && (
        <>
          <p className="mt-2 max-w-[62ch] text-[13px] text-ink-3">
            Drag the tiles into the order you want. Faded ones have nothing in
            them today &mdash; they will appear here when they do.
          </p>
          <DashboardArrange tiles={arranging} onChange={setArranging} />
          {save.isError && (
            <p className="mt-3 text-[13.5px] text-crit">
              That did not save, so nothing has changed. Try again in a moment.
            </p>
          )}
        </>
      )}

      {arranging === null && (
        <>
          {open.isError && (
            <p className="mt-3 text-[13.5px] text-crit">
              Your tiles could not be loaded, so there is nothing to arrange yet.
            </p>
          )}

          {what.isPending && <p className="mt-6 text-[13.5px] text-ink-3">Looking&hellip;</p>}

          {/*
            D-209: the whole read failing must never render as the empty state,
            because the empty state here says the studio is in order.
          */}
          {what.isError && (
            <p className="mt-6 max-w-[60ch] text-[13.5px] text-ink-2">
              This could not be loaded, so it is not a list of nothing. Try again
              in a moment.
            </p>
          )}

          {what.isSuccess && tiles.length === 0 && (
            <div className="mt-6">
              <p className="text-[15px] text-ink">Nothing needs you right now.</p>
              <p className="mt-1 max-w-[60ch] text-[13.5px] text-ink-3">
                Attendance is up to date, nobody is waiting on an answer, and
                there is no feedback to read.
              </p>
            </div>
          )}

          {tiles.length > 0 && (
            <ul className="mt-6 grid w-full list-none grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3 p-0">
              {tiles.map(tile => {
                const route = routeOf(tile.key)

                /*
                  `failed` is read before `count`, always. A failed tile carries
                  a null count rather than 0 precisely so a screen filtering on
                  the number cannot delete the tile that says a source is broken.
                */
                const body = (
                  <>
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-[3px] bg-[var(--tile-display)]"
                    />

                    <span className="block text-[11.5px] font-medium text-ink-2">
                      {titleOf(tile.key)}
                    </span>

                    <span className="mt-1.5 block text-[27px] leading-none font-bold tracking-tight tabular-nums text-ink">
                      {tile.failed ? '—' : tile.count}
                    </span>

                    <span className="mt-1 block text-[11.5px] text-ink-3">{tile.about}</span>
                  </>
                )

                const shell =
                  'tile-tint relative block w-full overflow-hidden rounded-[10px] border border-line bg-surface px-4 py-3.5 pl-[17px]'
                const tint = { '--tile-color': tintOf(tile.key) } as React.CSSProperties

                return (
                  <li key={tile.key} className="w-full">
                    {/*
                      A tile she cannot act on is not a link, and neither is one
                      whose source just refused — the screen it would open is
                      the thing that failed.
                    */}
                    {route === null || tile.failed ? (
                      <div className={shell} style={tint}>
                        {body}
                      </div>
                    ) : (
                      <Link to={route} className={`${shell} no-underline hover:border-line-2`} style={tint}>
                        {body}
                      </Link>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {/*
            *What needs your attention*, from the deck: the named things behind
            the figures, ranked. **A tile says two; this says who.**
          */}
          {what.data && what.data.attention.length > 0 && (
            <section className="mt-8">
              <div className="flex flex-wrap items-baseline gap-x-3">
                <h2 className="text-[11.5px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                  What needs your attention
                </h2>
                <span className="text-[11.5px] text-ink-3">Most urgent first</span>
              </div>

              <ul className="mt-2 w-full list-none rounded-[10px] border border-line bg-surface p-0">
                {what.data.attention.map((item, at) => (
                  <li key={`${item.key}-${at}`} className="w-full border-b border-line last:border-b-0">
                    <AttentionRow item={item} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/*
            *Today at the studio*, from the deck. **Context, not a tile**: it is
            never hidden and never arranged, because it says what kind of
            evening it is rather than asking her to do something.

            It renders below the tiles rather than beside them, because at phone
            width a side column is a second column nobody asked for — the deck's
            two-up layout assumed a desktop.
          */}
          {what.data?.studio && what.data.studio.tonight.length > 0 && (
            <section className="mt-8">
              <h2 className="text-[11.5px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                Today at the studio
              </h2>

              <ul className="mt-2 w-full list-none rounded-[10px] border border-line bg-surface p-0">
                {what.data.studio.tonight.map(session_ => (
                  <li
                    key={session_.occurrenceId}
                    className="flex w-full items-center gap-3 border-b border-line px-4 py-2.5 last:border-b-0"
                  >
                    <span className="w-[4.5rem] shrink-0 text-[11.5px] tabular-nums text-ink-3">
                      {formatTime(session_.startTime) ?? '—'}
                    </span>

                    {/*
                      Class names are machine-shaped and do not wrap on their
                      own: `COMP_TECH_BALLET_1PRETEAM` is one unbreakable word
                      to CSS, and the real data has 25-character tokens.
                    */}
                    <span className="min-w-0 flex-1 wrap-anywhere text-[13px] text-ink">
                      {session_.className}
                    </span>

                    {session_.teachers.length > 0 && (
                      <span className="hidden shrink-0 text-[11.5px] text-ink-3 sm:inline">
                        {session_.teachers.join(', ')}
                      </span>
                    )}

                    {session_.room && (
                      <span
                        className="room-tint shrink-0 rounded px-1.5 py-0.5 text-[11px] text-[var(--room-display)]"
                        style={{ '--room-color': session_.roomColor ?? undefined } as React.CSSProperties}
                      >
                        {session_.room}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}

/**
 * One line of *What needs your attention*.
 *
 * **The dot carries the severity and the rail colour carries the source**, so a
 * glance says both how loud this is and which figure it is an example of.
 *
 * `describeClass` renders a Jackrabbit name the way every other screen does
 * (D-579, D-190) — `COMP_SOLO | VIOLET | WED_330 | MADDIE` is four lines of raw
 * string and the distinguishing part is at the *end*, so printing it verbatim
 * is how a list becomes unreadable. A title that is not a class name passes
 * through unchanged, which is exactly what that function already guarantees
 * (D-581).
 */
function AttentionRow({ item }: { item: DashboardItem }) {
  const described = describeClass(item.title)
  const label = described.isTheirs ? item.title : described.label
  const who = described.isTheirs ? null : described.who

  const dot =
    item.severity === 'critical'
      ? 'bg-crit'
      : item.severity === 'warning'
        ? 'bg-warn'
        : 'bg-ink-3'

  const body = (
    <div className="flex w-full items-start gap-3 px-4 py-2.5">
      <span className={`mt-[7px] size-[7px] shrink-0 rounded-full ${dot}`} aria-hidden="true" />

      <span className="min-w-0 flex-1">
        <span className="block wrap-anywhere text-[13.5px] font-semibold text-ink">
          {label}
          {who && <span className="font-normal text-ink-2"> · {who}</span>}
        </span>
        {item.detail && (
          <span className="mt-0.5 block wrap-anywhere text-[12.5px] text-ink-3">{item.detail}</span>
        )}
      </span>

      {item.href && (
        <span className="shrink-0 self-center text-[12.5px] font-semibold text-accent">Open &rarr;</span>
      )}
    </div>
  )

  return item.href ? (
    <Link to={item.href} className="block w-full no-underline hover:bg-surface-2">
      {body}
    </Link>
  ) : (
    body
  )
}
